import type { RecommendedAccosAPIResponse } from '@/scopes/products/infrastructure/api/recommended-accos/types';
import type { Mealplan, RecommendedAcco } from '@/scopes/products/domain/types';
import type { RoomOccupancy } from '@/domain/shared/types';
import { sub } from 'date-fns';
import { CHILDREN_MAX_AGE, CHILDREN_MIN_AGE } from '@/domain/shared/constants';

export interface RecommendedAccosResponseMapper {
  map: (apiResponse: RecommendedAccosAPIResponse) => RecommendedAcco[];
}

export const mapParticipants = (birthdates: string[]): RoomOccupancy[] => {
  const adultAgeThreshold = sub(new Date(), { years: CHILDREN_MAX_AGE, days: 1 });
  const childrenAgeThreshold = sub(new Date(), { years: CHILDREN_MIN_AGE, days: 1 });

  const participants: RoomOccupancy = {
    adults: 0,
    children: [],
    babies: [],
  };

  for (const birthdateStr of birthdates) {
    const birthdate = new Date(birthdateStr);

    if (birthdate < adultAgeThreshold) {
      participants.adults += 1;
    } else if (birthdate < childrenAgeThreshold) {
      participants.children.push(birthdate);
    } else {
      participants.babies.push(birthdate);
    }
  }

  return [participants];
};

// TODO: Remove this hardcoded mapping once the API returns the mealplan names
const mapMealplan = (mealplanCode: string): Mealplan => {
  let name: string;

  switch (mealplanCode) {
    case 'LG':
      name = 'Logies';
      break;
    case 'LO':
      name = 'Logies ontbijt';
      break;
    case 'HP':
      name = 'Halfpension';
      break;
    case 'VP':
      name = 'Volpension';
      break;
    case 'AI':
      name = 'All-inclusive';
      break;
    case 'UA':
      name = 'Ultra all inclusive';
      break;
    default:
      name = mealplanCode;
      break;
  }

  return {
    code: mealplanCode,
    name,
  };
};

export const createRecommendedAccosResponseMapper: () => RecommendedAccosResponseMapper = () => {
  function map({ results: data }: RecommendedAccosAPIResponse): RecommendedAcco[] {
    if (!data) {
      return [];
    }

    return data.map<RecommendedAcco>((item) => {
      const mappedItem: RecommendedAcco = {
        id: item.accommodationId,
        name: item.name,
        imageUrl: item.image,
        rating: item.rating,
        city: item.cityName,
        region: item.regionName,
        country: item.countryName,
        url: item.url,
        price: {
          averagePrice: item.price.averagePrice,
          currencySettings: {
            symbol: item.price.currencySettings.symbol,
            symbolPosition: item.price.currencySettings.symbolPosition,
            numberOfDecimals: item.price.currencySettings.numberOfDecimals,
          },
          includedServices: { ...item.price.includedServices },
          staticText: item.price.staticText,
          departureDate: item.price.acmInformation.departureDateRaw,
          durationInDays: item.price.acmInformation.durationRaw,
          mealplan: mapMealplan(item.price.acmInformation.mealplanCode),
          transportType: item.price.acmInformation.transportType,
          participants: mapParticipants(item.price.acmInformation.birthdates),
        },
        userRating: item.userRating,
        usps: item.usps,
      };

      if (item.price.priceDiscounts) {
        mappedItem.price.discount = {
          percentage: item.price.priceDiscounts.discountPercentage,
          originalPrice: item.price.priceDiscounts.originalPrice,
        };
      }

      if (item.collection) {
        mappedItem.collection = {
          id: item.collection.id,
          name: item.collection.name,
        };
      }

      return mappedItem;
    });
  }

  return { map };
};
